<template>
    <vx-expandable-card>
        <template v-slot:header> {{ resourcesMetaDataMap[props.resourceType].title }} </template>
        <template v-slot:content>
            <div class="flex flex-col gap-6 mt-3">
                <vx-alert v-if="!model?.source_team?.id" :closeable="false"> No source team selected. </vx-alert>
                <div v-else-if="loadingResource" class="text-center py-10">
                    <vx-icon-loading class="w-9 h-9 animate-loading-spin"></vx-icon-loading>
                </div>
                <vx-alert v-else-if="!options.length" :closeable="false">
                    Source team has no {{ resourcesMetaDataMap[props.resourceType].title.toLowerCase() }}.
                </vx-alert>
                <template v-else>
                    <div class="flex items-center justify-between">
                        <div>
                            <vx-label :id="cloneAllLabelId" size="md"
                                >Clone All {{ resourcesMetaDataMap[props.resourceType].title }}</vx-label
                            >
                            <div :id="cloneAllDescriptionId" class="text-sm text-slate-700">
                                Clone all {{ resourcesMetaDataMap[props.resourceType].title.toLowerCase() }} from source
                                team?
                            </div>
                        </div>
                        <vx-toggle
                            v-model="cloneAll"
                            :aria-labelledby="cloneAllLabelId"
                            :aria-describedby="cloneAllDescriptionId"
                            size="lg"
                        ></vx-toggle>
                    </div>
                    <div
                        v-if="cloneAll && displayFirstError(validate[props.resourceType])"
                        class="pl-0.5 text-xs text-rose-800"
                    >
                        {{ displayFirstError(validate[props.resourceType]) }}
                    </div>
                    <vx-selectable
                        v-if="!cloneAll"
                        v-model="model[props.resourceType]"
                        :options="options"
                        :label="resourcesMetaDataMap[props.resourceType].optionLabel"
                        multiple
                        size="lg"
                        :closeOnSelect="false"
                        :error="displayFirstError(validate[props.resourceType])"
                    ></vx-selectable>
                </template>
            </div>
        </template>
    </vx-expandable-card>
</template>
<script setup>
import { VxExpandableCard, VxSelectable, VxToggle, VxLabel, VxAlert, VxIconLoading } from '@voxie/frontend-components';
import useVuelidate from '@vuelidate/core';
import { helpers, maxLength } from '@vuelidate/validators';
import { ref, useId, watch } from 'vue';
import { adminCloningClient } from '~/services';
import { displayFirstError } from '~/utils/validation';

const props = defineProps({
    resourceType: {
        type: String,
        required: true,
        validator: (value) =>
            ['automations', 'campaigns', 'segments', 'snippets', 'team_custom_attributes'].includes(value),
    },
    limit: {
        type: Number,
        required: true,
    },
});

const resourcesMetaDataMap = ref({
    automations: {
        title: 'Automations',
        apiMethodName: 'getClonableAutomations',
        optionLabel: 'name',
    },
    campaigns: {
        title: 'Campaigns',
        apiMethodName: 'getClonableCampaigns',
        optionLabel: 'name',
    },
    segments: {
        title: 'Segments',
        apiMethodName: 'getClonableSegments',
        optionLabel: 'name',
    },
    snippets: {
        title: 'Snippets',
        apiMethodName: 'getClonableSnippets',
        optionLabel: 'title',
    },
    team_custom_attributes: {
        title: 'Team Custom Attributes',
        apiMethodName: 'getClonableTeamCustomAttributes',
        optionLabel: 'key',
    },
});

const model = defineModel('form', {
    type: Object,
    required: true,
});

const validate = useVuelidate(
    {
        [props.resourceType]: {
            maxLength: helpers.withMessage(
                `${resourcesMetaDataMap.value[props.resourceType].title} should not contain more than ${props.limit} ${
                    props.resourceType
                }.`,
                maxLength(props.limit)
            ),
        },
    },
    model
);

const cloneAll = ref(false);
const cloneAllLabelId = useId();
const cloneAllDescriptionId = useId();
const options = ref([]);
const loadingResource = ref(false);
const loadResource = () => {
    if (loadingResource.value || !model.value?.source_team?.id) {
        return;
    }

    loadingResource.value = true;

    adminCloningClient[resourcesMetaDataMap.value[props.resourceType].apiMethodName](model.value?.source_team?.id)
        .then((response) => (options.value = response.data))
        .finally(() => (loadingResource.value = false));
};

watch(
    () => model.value?.source_team?.id,
    (value) => {
        model.value[props.resourceType] = [];

        if (value) {
            loadResource();
        }
    },
    {
        immediate: true,
    }
);

watch(
    () => cloneAll.value,
    (value) => {
        if (value) {
            model.value[props.resourceType] = options.value;
        } else {
            model.value[props.resourceType] = [];
        }
    }
);
</script>
